import { useCloseDeletedSelection } from "../../../components/common/useCloseDeletedSelection";
import { Alert, Button, Col, FormControl, FormGroup, FormLabel, FormSelect, Row, Table, Form } from "react-bootstrap";
import { IFieldEditor, IFormField } from "../../../../models/Interfaces";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faEdit, faPlus, faSave, faTimes } from "@fortawesome/free-solid-svg-icons";
import { useEffect, useRef, useState } from "react";
import { createFormValidation } from "../../../../helpers/formValidationHelper";
import FormCheckInput from "react-bootstrap/esm/FormCheckInput";
import { Core } from "@form-validation/bundle/full";
import { Bootstrap5 } from "@form-validation/plugin-bootstrap5";
import { Trigger } from "@form-validation/plugin-trigger";
import { SubmitButton } from "@form-validation/plugin-submit-button";

const FormFieldsEditor = (props: IFieldEditor) => {

  const [ selectedItem, setSelectedItem ] = useState<IFormField | null>(null);
  const [ fields, setFields ] = useState<Array<IFormField>>([]);
  const [ editorOpen, setEditorOpen ] = useState<boolean>(false);
  const [ fieldError, setFieldError ] = useState("");

  const formRef = useRef<HTMLFormElement | null>(null);
  const fvRef = useRef<Core | null>(null);

  const setFormValidation = () => {
    if (formRef.current) {
      fvRef.current = createFormValidation(formRef.current, {
        fields: {
          Name: {
            validators: {
              notEmpty: { message: "enter a name to identify this form when submitted"}
            }
          },
          Label: {
            validators: {
              notEmpty: { message: "enter a field label to display"}
            }
          },
          ValidationMessage: {
            validators: {
              callback: {
                callback: ((inputelement: HTMLInputElement) => {
                  const validator = { valid: true, message: "" };
                  const regexpinput = document.getElementById("ValidationRegEx") as HTMLInputElement;
                  const isrequired = document.getElementById("Required") as HTMLInputElement;
                  const invalidmessage: string = "validation message is required when a validator expression has been set or field is required";

                  if (regexpinput && isrequired) {
                    if ((regexpinput.value.length > 0 && inputelement.value.length === 0) ||
                        (isrequired.checked && inputelement.value.length === 0)) {
                      validator.valid = false;
                      validator.message = invalidmessage;
                    }
                  }
                  return validator;
                })
              }
            }
          },
          SelectOptions: {
            validators: {
              callback: {
                callback: (field: any) => {
                  const validator = {
                    valid: true,
                    message: ""
                  };

                  if ( selectedItem?.Type && selectedItem.Type  === "select") {
                    if (!field.value || field.value.length <= 0) {
                      validator.valid = false;
                      validator.message = "Select options are required for a select list field";
                    }
                  }
                  return validator;
                }
              }
            }
          },
          ValidationRegEx: {
            validators: {
              callback: {
                callback: ((inputelement: HTMLInputElement) => {
                  fvRef.current?.revalidateField("ValidationMessage");
                })
              }
            }
          },
          Required: {
            validators: {
              callback: {
                callback: ((inputelement: HTMLInputElement) => {
                  fvRef.current?.revalidateField("ValidationMessage");
                })
              }
            }
          }
        },
        plugins: {
          bootstrap5: new Bootstrap5(),
          trigger: new Trigger(),
          submitButton: new SubmitButton()
        }
      });
    }
  };

  const addItem = () => {
    const newid = fields && fields.length > 0
                  ? Math.max(...fields.map(f => f.Id)) + 1
                  : 1;

    const newitem: IFormField = {
      Id: newid,
      Label: "",
      LabelPosition: "None",
      LabelClass: "",
      Name: "",
      Required: false,
      Type: "input",
      ValidationMessage: "",
      ValidationRegEx: "",
      SelectOptions: ""
    };

    setSelectedItem(newitem);
    openCloseEditor(true);
  };

  const editItem = (item: IFormField) => {
    setSelectedItem(item);
    openCloseEditor(true);
  }

  const closeDeletedSelection = useCloseDeletedSelection(selectedItem, (item) => item.Id, () => openCloseEditor(false));

  const deleteItem = (item: IFormField) => {
    const updated = fields.filter(f => f.Id !== item.Id);
    updateFields(updated);
    closeDeletedSelection(item.Id);
  };

  const saveItem = async () => {

    if (fvRef.current) {
      const valid = await fvRef.current.validate();

      if (valid === "Valid") {
        if (selectedItem !== null) {
          const name = selectedItem.Name.trim().toLowerCase();
          if (fields.some(field => field.Id !== selectedItem.Id && field.Name.trim().toLowerCase() === name)) {
            setFieldError("Each field must have a unique name.");
            return;
          }
          setFieldError("");

          if (selectedItem.Type === "select" && selectedItem.SelectOptions) {
            selectedItem.SelectOptions = selectedItem.SelectOptions.replace(/\n/g, "|");
          }

          const updated = fields.some(f => f.Id === selectedItem.Id)
                          ? fields.map(f => f.Id === selectedItem.Id ? selectedItem : f)
                          : [...fields, selectedItem];

          updateFields(updated);
          openCloseEditor(false);
        }
      }
    }
  };

  const setFieldType = (evt: React.ChangeEvent<HTMLSelectElement>) => {
    const type = evt.target.value as "select" | "text" | "input" | "tel" | "email";

    setSelectedItem(prev => ({
      ...prev as IFormField,
      Type: type
    }));
  };

  const setItemProperty = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { value, name } = event.target;
    setFieldError("");

    setSelectedItem(prev => ({
      ...prev as IFormField,
      [name]: value
    }))
  };

  const setRequired = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.target;

    setSelectedItem(prev => ({
      ...prev as IFormField,
      Required: checked
    }));
  };
  
  const updateFields = (updated: IFormField[]): void => {
    setFields(updated);
    props.onValueChange(props.property.Id, JSON.stringify(updated));
  };

  const openCloseEditor = (open: boolean) : void => {
    setFieldError("");
    if (props.onEditorOpen) {
      props.onEditorOpen(open);
    }

    setEditorOpen(open);

    if (!open) {
      setSelectedItem(null);
    }
  };

  useEffect(() => {
    if (!editorOpen) return;
    setFormValidation();
    return () => {
      fvRef.current?.destroy();
      fvRef.current = null;
    };
  }, [editorOpen, selectedItem?.Type]);

  useEffect(() => {
    try {
      const parsed = JSON.parse(props.property.PublishedValue || "[]");
      setFields(Array.isArray(parsed) ? parsed : []);
    } catch {
      setFields([]);
    }
  }, [props.property.Id, props.property.PublishedValue]);

  return (
    <>
      { !editorOpen && (
        <>
          <Row>
            <Col xs={12} className="mt-3">
              <Button variant="outline-secondary" type="button" onClick={addItem}>
                <FontAwesomeIcon icon={faPlus} />&nbsp;Add Field
              </Button>
            </Col>
          </Row>    
          {fields.length > 0 && (
            <Row className="mt-3">
              <Col md={12}>
                <Table data-bs-toggle="table" className="table-bordered table-hover">
                  <thead className="alert alert-primary">
                    <tr>
                      <th scope="col">Name</th>
                      <th scope="col">Label</th>
                      <th scope="col">Type</th>
                      <th scope="col">Validation Expression</th>
                      <th scope="col">Validation Message</th>
                      <th scope="col">Required</th>
                      <th scope="col"></th>
                    </tr>
                  </thead>
                  <tbody>
                    { 
                      fields.map(fld => (
                        <tr key={`FORMFIELD-${fld.Id}`} id={`FORMFIELD-${fld.Id}`}>
                          <td>{fld.Name}</td>
                          <td>{fld.Label}</td>
                          <td>{fld.Type}</td>
                          <td>{fld.ValidationRegEx}</td>
                          <td>{fld.ValidationMessage}</td>
                          <td>
                            {
                              fld.Required ? 
                              (
                                <FontAwesomeIcon size="2x" icon={faCheck} className="text-success" />
                              )
                              :
                              (
                                <FontAwesomeIcon size="2x" icon={faTimes} className="text-danger" />
                              )
                            }
                          </td>
                          <td>
                            <div className="hstack">
                              <Button 
                                variant="outline-secondary" 
                                type="button" 
                                className="btn-nomin m-1" 
                                title="edit field" 
                                onClick={() => editItem(fld)}>
                                <FontAwesomeIcon icon={faEdit} />
                              </Button>
                              <Button 
                                variant="outline-secondary" 
                                type="button" 
                                className="btn-nomin m-1" 
                                title="remove field" 
                                onClick={() => deleteItem(fld)}>
                                <FontAwesomeIcon icon={faTimes} />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    }
                  </tbody>          
                </Table>
              </Col>
            </Row>
          )}

          {fields.length <= 0 && (
            <Alert variant="info" className="p-3 mt-3 text-center">
              <span>No form fields configured</span>
            </Alert>
          )}
        </>
      )}

      { (editorOpen && selectedItem !== null) && (
        <Form onSubmit={(event) => { event.preventDefault(); void saveItem(); }} data-nested-editor-open="true" id="FieldsForm" name="FieldsForm" ref={formRef}>
          <FormGroup>
            {fieldError && <Alert variant="danger">{fieldError}</Alert>}
            <Row>
              <Col md={6} xs={12} className="mt-3">
                <FormLabel htmlFor="Name">Field name</FormLabel>
                <FormControl 
                  id="Name"
                  name="Name"
                  type="text"
                  value={selectedItem.Name ?? ""}
                  onChange={setItemProperty} 
                  placeholder="form field name..."
                />
              </Col>
              <Col md={6} xs={12} className="mt-3">
                <FormLabel htmlFor="Label">Field label</FormLabel>
                <FormControl 
                  id="Label"
                  name="Label"
                  type="text"
                  value={selectedItem.Label}
                  onChange={setItemProperty}
                  placeholder="Field label..."
                />
              </Col>
            </Row>
            <Row>
              <Col md={6} xs={12} className="mt-3">
                <FormLabel htmlFor="LabelPosition">Label position</FormLabel>
                <FormSelect id="LabelPosition" name="LabelPosition" value={selectedItem.LabelPosition ?? "None"}
                  onChange={(event) => setSelectedItem(prev => ({ ...prev as IFormField,
                    LabelPosition: event.target.value as IFormField["LabelPosition"] }))}>
                  <option value="None">None</option>
                  <option value="Above">Above</option>
                  <option value="Left">Left</option>
                </FormSelect>
              </Col>
              <Col md={6} xs={12} className="mt-3">
                <FormLabel htmlFor="LabelClass">Label CSS classes</FormLabel>
                <FormControl id="LabelClass" name="LabelClass" value={selectedItem.LabelClass ?? ""}
                  onChange={setItemProperty} placeholder="e.g. fw-bold text-primary" />
              </Col>
            </Row>
            <Row>
              <Col md={6} xs={12} className="mt-3">
                <FormLabel htmlFor="Type">Field type</FormLabel>
                <FormSelect 
                    id="Type"
                    name="Type"
                    title="Select field input type" 
                    value={selectedItem.Type ?? ""} 
                    onChange={setFieldType}>
                  <option value="input">Input Text</option>
                  <option value="text">Free Text</option>
                  <option value="email">Email address</option>
                  <option value="tel">Telephone number</option>
                  <option value="select">Select list</option>
                </FormSelect>
              </Col>

              { selectedItem.Type === "select" && (
                <Col md={6} xs={12} className="mt-3">
                  <FormLabel >Select options</FormLabel>
                  <textarea 
                    id='SelectOptions'
                    name="SelectOptions"
                    onChange={setItemProperty}
                    value={selectedItem.SelectOptions?.replace(/\|/g, "\n") ?? ""}
                    className="form-control"
                    rows={6}
                    placeholder="Add a select option on a new line...">
                  </textarea>
                </Col>
              )}
            </Row>
            <Row>
              <Col md={6} xs={12} className="mt-3">
                <FormLabel>Validation check (regular expression)</FormLabel>
                <FormControl 
                  id="ValidationRegEx"
                  type="text" 
                  name="ValidationRegEx"
                  value={selectedItem.ValidationRegEx ?? ""}
                  onChange={setItemProperty}
                  placeholder="add validation expression...">
                </FormControl>
              </Col>
              <Col md={6} xs={12} className="mt-3">
                <FormLabel>Validation message</FormLabel>
                <FormControl 
                  id="ValidationMessage"
                  name="ValidationMessage"
                  value={selectedItem.ValidationMessage ?? ""}
                  onChange={setItemProperty}
                  type="text" 
                  placeholder="add invalid entry message...">
                </FormControl>
              </Col>
            </Row>
            <Row>
              <Col md={6} xs={12} className="mt-3">
                <FormLabel htmlFor="Required">Value required</FormLabel>
                <FormCheckInput 
                  id="Required"
                  name="Required"
                  checked={selectedItem.Required}
                  onChange={setRequired}
                  className="btn-nomin form-check">
                </FormCheckInput>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <hr />
              </Col>
            </Row>
            <Row>
              <Col md={12} className="text-end">
                <Button type="button" 
                  variant="outline-success" 
                  className="me-2" 
                  onClick={saveItem}
                  title="save field">
                  <FontAwesomeIcon icon={faSave} />&nbsp;Save
                </Button>
                <Button 
                  type="button" 
                  variant="outline-primary" 
                  title="close"
                  onClick={() => openCloseEditor(false)}>Close
                </Button>
              </Col>
            </Row>
          </FormGroup>
        </Form>
      )}
    </>
  );
}

export default FormFieldsEditor;
